import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { BaseFilterService } from '../../../shared/services/base-filter.service';

export interface DashboardFilters {
  frequency: string;
  division: string[];
  entity: string[];
  vendorGroup: string[];
  classification: string[];
  period: string[];
}

export interface FilterPayload {
  frequency: string;
  division: string | null;
  entityName: string | null;
  vendorName: string | null;
  classification: string | null;
  period: string | null;
}

const EMPTY_FILTERS: DashboardFilters = {
  frequency: 'Quarterly',
  division: [],
  entity: [],
  vendorGroup: [],
  classification: [],
  period: [],
};

@Injectable({ providedIn: 'root' })
export class DashboardFilterService extends BaseFilterService<DashboardFilters> {
  protected readonly storageKey = 'dashboard_filters';
  protected readonly emptyFilters = EMPTY_FILTERS;

  /** Currently selected recon metric card (null = no selection) */
  reconState = signal<string | null>(null);

  /** True while the vendor-summary grid data is loading */
  gridLoading = signal(false);

  /** Emits when cue cards should be refreshed (e.g. after workflow submission) */
  refreshCueCards$ = new Subject<void>();

  /** True after the first explicit applyFilters() call (prevents effect firing on init) */
  ready = signal(false);

  constructor() {
    super();
    this.init();
  }

  override applyFilters(filters: DashboardFilters): void {
    super.applyFilters(filters);
    if (!this.ready()) this.ready.set(true);
  }

  /** Full reset — call on logout so the singleton is clean for the next session */
  reset(): void {
    this.resetFilters();
    this.ready.set(false);
    this.reconState.set(null);
    this.gridLoading.set(false);
  }

  buildPayload(filters?: DashboardFilters): FilterPayload {
    const f = filters ?? this.filters();
    return {
      frequency: f.frequency || 'Quarterly',
      division: f.division.length ? f.division.join(',') : null,
      entityName: f.entity.length ? f.entity.join(',') : null,
      vendorName: f.vendorGroup.length ? f.vendorGroup.join(',') : null,
      classification: f.classification.length ? f.classification.join(',') : null,
      period: f.period.length ? f.period.join(',') : null,
    };
  }

  protected parseStored(parsed: any): DashboardFilters {
    return {
      frequency: typeof parsed.frequency === 'string' ? parsed.frequency : 'Quarterly',
      division: Array.isArray(parsed.division) ? parsed.division : [],
      entity: Array.isArray(parsed.entity) ? parsed.entity : [],
      vendorGroup: Array.isArray(parsed.vendorGroup) ? parsed.vendorGroup : [],
      classification: Array.isArray(parsed.classification) ? parsed.classification : [],
      period: Array.isArray(parsed.period) ? parsed.period : [],
    };
  }
}
